import React, { useState } from "react";

import styles from "../css/CreateProfile.module.css";
import personalStyles from "../css/PersonalDetailsStep.module.css";

import Button from "../../../../components/Button";
import ReadOnlyInput from "../../../../components/ReadOnlyInput";
import FormInput from "../../../../components/FormInput";
import Dropdown from "../../../../components/Dropdown";
import Modal from "../../../../components/Modal";
import { setProfileEnabled as apiSetProfileEnabled } from "../../../../api/profile/update_profile";
import { validateMainMemberId } from "../../../../api/profile/create_profile";

import { Trash2 } from "lucide-react";
import { AiFillEye } from "react-icons/ai";

export default function PersonalDetailsStep({
  isViewMode,
  viewPersonal,
  setViewPersonal,
  beneficiaries,
  beneficiariesCount,
  setBeneficiariesCount,
  setBeneficiaries,
  conditionsList,
  setConditionsList,
  allergiesList,
  setAllergiesList,
  setModalStep,
  modalMode,
  setShowAddPatient,
  setIsViewMode,
  selectedPatientId,
  onProfileEnabledChanged,
  onValidationError,
}) {
  const deriveDobFromSaId = (id) => {
    const clean = String(id || "").replace(/[^0-9]/g, "");
    if (clean.length < 6) return "";
    const yy = clean.slice(0, 2);
    const mm = clean.slice(2, 4);
    const dd = clean.slice(4, 6);
    const yearNum = Number(yy);
    if (!Number.isFinite(yearNum)) return "";
    const currentYear = new Date().getFullYear() % 100;
    const century = yearNum > currentYear ? 1900 : 2000;
    const fullYear = century + yearNum;
    return `${fullYear.toString().padStart(4, "0")}-${mm}-${dd}`;
  };

  const deriveSexFromSaId = (id) => {
    const clean = String(id || "").replace(/[^0-9]/g, "");
    if (clean.length < 10) return "";
    const genderDigits = Number(clean.slice(6, 10));
    if (!Number.isFinite(genderDigits)) return "";
    return genderDigits >= 5000 ? "Male" : "Female";
  };

  const deriveAgeFromDob = (dob) => {
    if (!dob) return "";
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age -= 1;
    }
    return age >= 0 ? age : "";
  };

  const handleAddDelivery = () => {
    setViewPersonal({
      ...viewPersonal,
      deliveryAddressesExtra: [...(viewPersonal.deliveryAddressesExtra || []), { street: "", suburb: "", city: "", postalCode: "" }],
    });
  };

  const handleChangeDelivery = (index, field, value) => {
    const extras = [...(viewPersonal.deliveryAddressesExtra || [])];
    extras[index] = { ...extras[index], [field]: value };
    setViewPersonal({ ...viewPersonal, deliveryAddressesExtra: extras });
  };

  const handleRemoveDelivery = (index) => {
    const extras = [...(viewPersonal.deliveryAddressesExtra || [])];
    extras.splice(index, 1);
    setViewPersonal({ ...viewPersonal, deliveryAddressesExtra: extras });
  };

  const [showProfileToggleConfirm, setShowProfileToggleConfirm] = useState(false);

  return (
    <>
      <div className={styles.modalHeader}>
        <div className={personalStyles.headerTitleRow}>
          <h2>
            {modalMode === "update"
              ? "Update Patient: Personal Details"
              : modalMode === "add"
                ? "Add Patient: Personal Details"
                : "View Patient: Personal Details"}
          </h2>
          {modalMode === "update" && (
            <button
              type="button"
              title={viewPersonal.profileEnabled === false ? "Enable profile" : "Disable profile"}
              onClick={() => setShowProfileToggleConfirm(true)}
              className={styles.eyeBtn || ""}
            >
              <AiFillEye />
            </button>
          )}
        </div>

        <button
          className={styles.closeX}
          aria-label="Close"
          onClick={() => { setShowAddPatient(false); setIsViewMode(false); }}
        >
          ×
        </button>
      </div>
      <div className={styles.modalBody}>
        <div className={styles.formGrid}>
          <div className={styles.col}>
            {isViewMode ? (
              <ReadOnlyInput label="Surname" value={viewPersonal.surname} />
            ) : (
              <FormInput
                label="Surname"
                type="text"
                name="surname"
                value={viewPersonal.surname || ""}
                onChange={(e) => setViewPersonal({ ...viewPersonal, surname: e.target.value })}
              />
            )}
            <div className={personalStyles.inlineTwo}>
              {isViewMode ? (
                <>
                  <ReadOnlyInput label="First Name(s)" value={viewPersonal.firstNames} />
                  <ReadOnlyInput label="Title" value={viewPersonal.title} />
                </>
              ) : (
                <>
                  <FormInput
                    label="First Name(s)"
                    type="text"
                    name="firstname"
                    value={viewPersonal.firstNames || ""}
                    onChange={(e) => setViewPersonal({ ...viewPersonal, firstNames: e.target.value })}
                  />
                  <Dropdown
                    label="Title"
                    name="title"
                    options={["Mr", "Mrs", "Miss", "Dr"]}
                    value={viewPersonal.title || ""}
                    onChange={(val) => setViewPersonal({ ...viewPersonal, title: val })}
                    placeholder="Select"
                  />
                </>
              )}
            </div>
            {isViewMode ? (
              <ReadOnlyInput label="ID Number / Passport" value={viewPersonal.idNumber} />
            ) : (
              <div>
                <div className={personalStyles.idTypeRow}>
                  <label className={personalStyles.idTypeLabel}>
                    <input
                      type="radio"
                      name="main-id-type"
                      value="id"
                      checked={(viewPersonal.idType || "id") === "id"}
                      onChange={() => setViewPersonal({ ...viewPersonal, idType: "id" })}
                    />
                    ID Number
                  </label>
                  <label className={personalStyles.idTypeLabel}>
                    <input
                      type="radio"
                      name="main-id-type"
                      value="passport"
                      checked={viewPersonal.idType === "passport"}
                      onChange={() => setViewPersonal({ ...viewPersonal, idType: "passport" })}
                    />
                    Passport
                  </label>
                </div>
                <FormInput
                  label="ID Number / Passport"
                  type="text"
                  name="idnumber"
                  value={viewPersonal.idNumber || ""}
                  onChange={(e) => {
                    const raw = e.target.value || "";
                    const isId = (viewPersonal.idType || "id") === "id";
                    const v = isId ? raw.replace(/\D/g, "").slice(0, 13) : raw;

                    let next = { ...viewPersonal, idNumber: v };

                    if (isId && v.length === 13) {
                      const derivedDob = deriveDobFromSaId(v);
                      const derivedSex = deriveSexFromSaId(v);
                      const derivedAge = deriveAgeFromDob(derivedDob);

                      if (derivedDob) {
                        next = { ...next, dob: derivedDob };
                      }
                      if (derivedSex) {
                        next = { ...next, sex: derivedSex };
                      }
                      if (derivedAge !== "") {
                        next = { ...next, age: derivedAge };
                      }
                    }

                    setViewPersonal(next);
                  }}
                />
              </div>
            )}
            <div className={personalStyles.inlineThree}>
              {isViewMode ? (
                <>
                  <ReadOnlyInput label="Sex" value={viewPersonal.sex} />
                  <ReadOnlyInput label="Age" value={viewPersonal.age} />
                  <ReadOnlyInput label="Weight (Kg)" value={viewPersonal.weight} />
                </>
              ) : (
                <>
                  <Dropdown
                    label="Sex"
                    name="sex"
                    options={["Male", "Female", "Other"]}
                    value={viewPersonal.sex || ""}
                    onChange={(val) => setViewPersonal({ ...viewPersonal, sex: val })}
                    placeholder="Select"
                  />
                  <FormInput
                    label="Age"
                    type="number"
                    name="age"
                    min={0}
                    value={viewPersonal.age ?? ""}
                    onChange={(e) => {
                      const n = Math.max(0, Number(e.target.value || 0));
                      setViewPersonal({ ...viewPersonal, age: Number.isFinite(n) ? n : 0 });
                    }}
                  />
                  <FormInput
                    label="Weight (Kg)"
                    type="number"
                    name="weightinkg"
                    min={0}
                    step="0.01"
                    value={viewPersonal.weight ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || value === ".") {
                        setViewPersonal({ ...viewPersonal, weight: value });
                        return;
                      }
                      const n = Math.max(0, parseFloat(value));
                      setViewPersonal({ ...viewPersonal, weight: Number.isFinite(n) ? n : 0 });
                    }}
                  />
                </>
              )}
            </div>
            {isViewMode ? (
              <div>
                <label>Home Address</label>
                <div className={personalStyles.addressStack}>
                  <ReadOnlyInput label="Street" value={viewPersonal.homeStreet} />
                  <ReadOnlyInput label="Suburb" value={viewPersonal.homeSuburb} />
                  <ReadOnlyInput label="City" value={viewPersonal.homeCity} />
                  <ReadOnlyInput label="Postal Code" value={viewPersonal.homePostalCode} />
                </div>
              </div>
            ) : (
              <div>
                <label>Home Address</label>
                <div className={personalStyles.addressStack}>
                  <FormInput
                    label="Street"
                    type="text"
                    name="homeStreet"
                    value={viewPersonal.homeStreet || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setViewPersonal((prev) => ({
                        ...prev,
                        homeStreet: v,
                        // Keep delivery in sync while it still matches the previous home value or is empty
                        deliveryStreet:
                          !prev.deliveryStreet || prev.deliveryStreet === prev.homeStreet
                            ? v
                            : prev.deliveryStreet,
                      }));
                    }}
                  />
                  <FormInput
                    label="Suburb"
                    type="text"
                    name="homeSuburb"
                    value={viewPersonal.homeSuburb || ""}
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      const v = raw.replace(/[^a-zA-Z\s]/g, "");
                      setViewPersonal((prev) => ({
                        ...prev,
                        homeSuburb: v,
                        deliverySuburb:
                          !prev.deliverySuburb || prev.deliverySuburb === prev.homeSuburb
                            ? v
                            : prev.deliverySuburb,
                      }));
                    }}
                  />
                  <FormInput
                    label="City"
                    type="text"
                    name="homeCity"
                    value={viewPersonal.homeCity || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setViewPersonal((prev) => ({
                        ...prev,
                        homeCity: v,
                        deliveryCity:
                          !prev.deliveryCity || prev.deliveryCity === prev.homeCity
                            ? v
                            : prev.deliveryCity,
                      }));
                    }}
                  />
                  <FormInput
                    label="Postal Code"
                    type="text"
                    name="homePostalCode"
                    value={viewPersonal.homePostalCode || ""}
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      const v = raw.replace(/\D/g, "").slice(0, 4);
                      setViewPersonal((prev) => ({
                        ...prev,
                        homePostalCode: v,
                        deliveryPostalCode:
                          !prev.deliveryPostalCode || prev.deliveryPostalCode === prev.homePostalCode
                            ? v
                            : prev.deliveryPostalCode,
                      }));
                    }}
                    maxLength={4}
                  />
                </div>
              </div>
            )}
            {isViewMode ? (
              <ReadOnlyInput label="Allergies" value={(allergiesList || []).filter(Boolean).join(", ")} />
            ) : (
              <div className={personalStyles.chronicStack}>
                <div className={personalStyles.inlineWithButton}>
                  <div className={personalStyles.matchFirstWidth}>
                    <FormInput
                      label="Allergies"
                      type="text"
                      name="allergies0"
                      value={allergiesList?.[0] || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAllergiesList((prev) => {
                          const next = Array.isArray(prev) ? [...prev] : [""];
                          next[0] = v;
                          return next;
                        });
                      }}
                    />
                  </div>
                  <Button
                    variant="primary"
                    className={personalStyles.plusBtn}
                    onClick={() => setAllergiesList((prev) => [...(prev || []), ""])}
                  >
                    +
                  </Button>
                </div>
                {(allergiesList || []).slice(1).map((val, idx) => (
                  <div key={idx} className={personalStyles.inlineWithButton}>
                    <div className={personalStyles.matchFirstWidth}>
                      <FormInput
                        label=""
                        placeholder="Allergy"
                        type="text"
                        name={`allergy${idx + 1}`}
                        value={val}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAllergiesList((prev) => {
                            const next = [...(prev || [])];
                            next[idx + 1] = v;
                            return next;
                          });
                        }}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      className={personalStyles.minusBtn}
                      onClick={() =>
                        setAllergiesList((prev) => (prev || []).filter((_, i) => i !== idx + 1))
                      }
                    >
                      <Trash2 size={18} color="#ffffff" strokeWidth={2} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.col}>
            {isViewMode ? (
              <div>
                <label>Delivery Address</label>
                <div className={personalStyles.addressStack}>
                  <ReadOnlyInput label="Street" value={viewPersonal.deliveryStreet} />
                  <ReadOnlyInput label="Suburb" value={viewPersonal.deliverySuburb} />
                  <ReadOnlyInput label="City" value={viewPersonal.deliveryCity} />
                  <ReadOnlyInput label="Postal Code" value={viewPersonal.deliveryPostalCode} />
                </div>
              </div>
            ) : (
              <div>
                <label>Delivery Address</label>
                <div className={personalStyles.addressStack}>
                  <FormInput
                    label="Street"
                    type="text"
                    name="deliveryStreet"
                    value={viewPersonal.deliveryStreet || ""}
                    onChange={(e) =>
                      setViewPersonal({ ...viewPersonal, deliveryStreet: e.target.value })
                    }
                  />
                  <FormInput
                    label="Suburb"
                    type="text"
                    name="deliverySuburb"
                    value={viewPersonal.deliverySuburb || ""}
                    onChange={(e) => {
                      const v = (e.target.value || "").replace(/[^a-zA-Z\s]/g, "");
                      setViewPersonal({ ...viewPersonal, deliverySuburb: v });
                    }}
                  />
                  <FormInput
                    label="City"
                    type="text"
                    name="deliveryCity"
                    value={viewPersonal.deliveryCity || ""}
                    onChange={(e) =>
                      setViewPersonal({ ...viewPersonal, deliveryCity: e.target.value })
                    }
                  />
                  <FormInput
                    label="Postal Code"
                    type="text"
                    name="deliveryPostalCode"
                    value={viewPersonal.deliveryPostalCode || ""}
                    onChange={(e) => {
                      const v = (e.target.value || "").replace(/\D/g, "").slice(0, 4);
                      setViewPersonal({ ...viewPersonal, deliveryPostalCode: v });
                    }}
                    maxLength={4}
                  />
                </div>
              </div>
            )}
            <div className={personalStyles.inlineTwo}>
              {modalMode !== "add" && (
                <ReadOnlyInput label="Profile Number" value={viewPersonal.profileNumber || ""} />
              )}
              {isViewMode ? (
                <ReadOnlyInput label="Email Address" value={viewPersonal.email} />
              ) : (
                <FormInput
                  label="Email Address"
                  type="email"
                  name="email"
                  value={viewPersonal.email || ""}
                  onChange={(e) => setViewPersonal({ ...viewPersonal, email: e.target.value })}
                />
              )}
            </div>
            <div className={personalStyles.inlineTwo}>
              {isViewMode ? (
                <>
                  <ReadOnlyInput label="Home/Cell Number" value={viewPersonal.cell} />
                  <ReadOnlyInput label="Employer (optional)" value={viewPersonal.employer} />
                </>
              ) : (
                <>
                  <FormInput label="Home/Cell Number" type="tel" name="contactnumber" value={viewPersonal.cell || ""} onChange={(e) => {
                    const v = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                    setViewPersonal({ ...viewPersonal, cell: v });
                  }} />
                  <FormInput label="Employer (optional)" type="text" name="employer" value={viewPersonal.employer || ""} onChange={(e) => setViewPersonal({ ...viewPersonal, employer: e.target.value })} />
                </>
              )}
            </div>
            <div className={personalStyles.inlineTwoAlign}>
              {isViewMode ? (
                <>
                  <ReadOnlyInput label="Chronic Conditions" value={viewPersonal.conditions} />
                  <ReadOnlyInput
                    label="Beneficiaries"
                    value={beneficiaries.filter((b) => b && b.enabled !== false).length}
                  />
                </>
              ) : (
                <>
                  <div className={personalStyles.chronicStack}>
                    <div className={personalStyles.inlineWithButton}>
                      <div className={personalStyles.matchFirstWidth}>
                        <FormInput
                          label="Chronic Conditions"
                          type="text"
                          name="chronicconditions"
                          value={conditionsList[0] || ""}
                          onChange={(e) => {
                            const v = (e.target.value || "").replace(/[^a-zA-Z\s,]/g, "");
                            setConditionsList((prev) => {
                              const next = [...prev];
                              next[0] = v;
                              return next;
                            });
                          }}
                        />
                      </div>
                      <Button
                        variant="primary"
                        className={personalStyles.plusBtn}
                        onClick={() => setConditionsList((prev) => [...prev, ""])}
                      >
                        +
                      </Button>
                    </div>
                    {conditionsList.slice(1).map((val, idx) => (
                      <div key={idx} className={personalStyles.inlineWithButton}>
                        <div className={personalStyles.matchFirstWidth}>
                          <FormInput
                            label=""
                            placeholder="Chronic Conditions"
                            type="text"
                            name={`conditions${idx + 1}`}
                            value={val}
                            onChange={(e) => {
                              const v = (e.target.value || "").replace(/[^a-zA-Z\s,]/g, "");
                              setConditionsList((prev) => {
                                const next = [...prev];
                                next[idx + 1] = v;
                                return next;
                              });
                            }}
                          />
                        </div>
                        <Button
                          variant="secondary"
                          className={personalStyles.minusBtn}
                          onClick={() => {
                            setConditionsList((prev) => prev.filter((_, i) => i !== idx + 1));
                          }}
                        >
                          <Trash2 size={18} color="#ffffff" strokeWidth={2} />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className={personalStyles.alignStartFull}>
                    <Dropdown
                      label="Beneficiaries"
                      name="beneficiaries"
                      options={["0", "1", "2", "3", "4+"]}
                      value={
                        beneficiariesCount === 0
                          ? "0"
                          : beneficiariesCount
                          ? beneficiariesCount >= 4
                            ? "4+"
                            : String(beneficiariesCount)
                          : "0"
                      }
                      onChange={(val) => {
                        const nextCount = val === "4+" ? 4 : Number(val);
                        setBeneficiariesCount(nextCount);
                        if (typeof setBeneficiaries === "function") {
                          setBeneficiaries((prev = []) => {
                            const baseAddress = {
                              homeStreet: viewPersonal.homeStreet || "",
                              homeSuburb: viewPersonal.homeSuburb || "",
                              homeCity: viewPersonal.homeCity || "",
                              homePostalCode: viewPersonal.homePostalCode || "",
                              deliveryStreet: viewPersonal.deliveryStreet || "",
                              deliverySuburb: viewPersonal.deliverySuburb || "",
                              deliveryCity: viewPersonal.deliveryCity || "",
                              deliveryPostalCode: viewPersonal.deliveryPostalCode || "",
                            };

                            const arr = Array.from({ length: nextCount }, (_, i) => {
                              const existing = prev[i] || {};
                              return {
                                ...baseAddress,
                                ...existing,
                                allergiesList: Array.isArray(existing.allergiesList)
                                  ? existing.allergiesList
                                  : [""],
                                enabled: existing.enabled !== false,
                              };
                            });

                            return arr;
                          });
                        }
                      }}
                      placeholder="Select"
                      className={personalStyles.beneficiariesSelect}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.modalFooter}>
        <Button
          variant="primary"
          onClick={async () => {
            if (isViewMode) {
              setModalStep(2);
              return;
            }

            const missing = [];

            if (!viewPersonal.surname) missing.push("Surname");
            if (!viewPersonal.firstNames) missing.push("First Name(s)");
            if (!viewPersonal.title) missing.push("Title");
            if (!viewPersonal.idNumber) missing.push("ID Number");
            if (!viewPersonal.sex) missing.push("Sex");
            if (viewPersonal.age === undefined || viewPersonal.age === null || viewPersonal.age === "") missing.push("Age");
            if (viewPersonal.weight === undefined || viewPersonal.weight === null || viewPersonal.weight === "") missing.push("Weight (Kg)");
            if (!viewPersonal.homeStreet) missing.push("Home Street");
            if (!viewPersonal.homeSuburb) missing.push("Home Suburb");
            if (!viewPersonal.homeCity) missing.push("Home City");
            if (!viewPersonal.homePostalCode) missing.push("Home Postal Code");
            if (!viewPersonal.email) missing.push("Email Address");
            if (!viewPersonal.cell) missing.push("Home/Cell Number");

            const email = (viewPersonal.email || "").trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (email && !emailRegex.test(email)) {

              const msg = "Please enter a valid Email Address.";
              if (typeof onValidationError === "function") {
                onValidationError("Validation error", msg);
              } else {
                alert(msg);
              }
              return;
            }

            const mainIdType = viewPersonal.idType || "id";
            const idNumber = (viewPersonal.idNumber || "").trim();

            if (mainIdType === "id" && idNumber) {
              try {
                await validateMainMemberId(idNumber);
              } catch (e) {
                const errData = e?.response?.data || {};
                const title = errData.error || "Main member ID validation";
                const reason = errData.reason || "Invalid South African ID number";
                const msg = reason ? `${title}: ${reason}` : title;
                if (typeof onValidationError === "function") {
                  onValidationError("Main member ID validation", msg);
                } else {
                  alert(msg);
                }
                return;
              }
            }

            if (missing.length) {

              const msg = `Please fill in the following required fields: ${missing.join(", ")}.`;

              if (typeof onValidationError === "function") {
                onValidationError("Validation error", msg);
              } else {
                alert(msg);
              }
              return;
            }

            setModalStep(2);
          }}
          className={styles.nextBtn}
        >
          Next
        </Button>
      </div>

      {modalMode === "update" && showProfileToggleConfirm && (
        <Modal onClose={() => setShowProfileToggleConfirm(false)} closeStyle="plain">
          <div className={personalStyles.toggleConfirmContent}>
            <h2 className={personalStyles.toggleConfirmTitle}>
              {viewPersonal.profileEnabled === false ? "Enable Profile" : "Disable Profile"}
            </h2>

            <p className={personalStyles.toggleConfirmText}>
              Are you sure you want to {viewPersonal.profileEnabled === false ? "enable" : "disable"} this profile?
            </p>

            <div className={personalStyles.toggleConfirmButtons}>
              <Button
                variant="approve"
                onClick={async () => {
                  const currentlyEnabled = viewPersonal.profileEnabled !== false;
                  const desired = !currentlyEnabled;

                  if (selectedPatientId) {
                    try {
                      await apiSetProfileEnabled(selectedPatientId, desired);
                    } catch (err) {
                      alert(err?.message || "Failed to update profile enabled status");
                      setShowProfileToggleConfirm(false);
                      return;
                    }
                  }

                  setViewPersonal({
                    ...viewPersonal,
                    profileEnabled: desired,
                  });
                  setShowProfileToggleConfirm(false);

                  if (typeof onProfileEnabledChanged === "function") {
                    onProfileEnabledChanged();
                  }
                }}
              >
                Yes
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowProfileToggleConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}